import type { Lesson } from './types'

export const otherRulesLessons: Lesson[] = [
  {
    id: 'raa-tafkhim-tarqiq',
    title: 'Pronouncing Raa: Heavy or Light',
    summary: 'Unlike most letters, ر changes weight depending on its vowel.',
    explanation: [
      'Most Arabic letters are consistently either heavy or light. Raa (ر) is one of the exceptions - like the laam in Allah\'s name, its weight depends on context.',
      'Heavy (tafkhim) - when raa carries a fatha or damma, or carries a sukun preceded by a fatha or damma.',
      'Light (tarqiq) - when raa carries a kasra, or carries a sukun preceded by an original kasra.',
      'As a beginner, the fastest way to get this right by ear is: raa next to an "a" or "u" sound is heavy and full; raa next to an "i" sound is light and thin.',
    ],
    colorLegend: [
      { color: 'heavy', label: 'Heavy raa (with/after fatha or damma)' },
      { color: 'light', label: 'Light raa (with/after kasra)' },
    ],
    examples: [
      { arabic: 'رَبِّ', transliteration: 'rabbi', meaning: 'my Lord', highlights: [{ text: 'رَ', color: 'heavy' }] },
      { arabic: 'الْقَمَرُ', transliteration: 'al-qamaru', meaning: 'the moon', highlights: [{ text: 'رُ', color: 'heavy' }] },
      { arabic: 'مِرْيَةٍ', transliteration: 'miryah', meaning: 'doubt', highlights: [{ text: 'رْ', color: 'light' }] },
      { arabic: 'فِرْعَوْنَ', transliteration: 'firʿawn', meaning: 'Pharaoh', highlights: [{ text: 'رْ', color: 'light' }] },
    ],
  },
  {
    id: 'tafkhim-tarqiq-overview',
    title: 'Heavy and Light Letters (Tafkhim & Tarqiq)',
    summary: 'Seven letters are always pronounced heavy; the rest are light by default.',
    explanation: [
      'Beyond the special cases of Raa and the laam in Allah\'s name, seven letters are always pronounced heavy (mufakhkham), remembered by the phrase خُصَّ ضَغْطٍ قِظْ (khuṣṣa ḍaghṭin qiẓ): خ ص ض غ ط ق ظ.',
      'These are pronounced with the back of the tongue raised toward the soft palate, giving a fuller, rounder, more resonant sound - and they keep this heaviness whether they carry fatha, kasra, or damma.',
      'Every other letter is light (muraqqaq) by default, pronounced thin and forward in the mouth.',
    ],
    colorLegend: [{ color: 'heavy', label: 'Always-heavy letters' }],
    examples: [
      { arabic: 'خَ ص ض غ ط ق ظ', transliteration: 'khā ṣ ḍ gh ṭ q ẓ', highlights: [{ text: 'خَ ص ض غ ط ق ظ', color: 'heavy' }] },
      { arabic: 'ضَالِّينَ', transliteration: 'ḍāllīn', meaning: 'those who are astray', highlights: [{ text: 'ضَ', color: 'heavy' }] },
    ],
  },
  {
    id: 'waqf-signs',
    title: 'Waqf Signs (Where to Stop)',
    summary: 'Small symbols printed in the Mushaf tell you where stopping is required, allowed, or best avoided.',
    explanation: [
      'A printed Mushaf includes small marks above certain words showing whether it is appropriate to pause reciting there. Recognizing these keeps meaning intact - stopping in the wrong place can accidentally change what a verse means.',
      'م (Waqf Lazim) - a required stop; continuing without pausing here would distort the meaning.',
      'لا - do not stop here; if you must pause (e.g. to breathe), go back and re-read from a safe point before continuing.',
      'ط (Waqf Mutlaq) - stopping is preferred here.',
      'ج (Waqf Ja\'iz) - permissible to stop or continue, both equally fine.',
      'ز (Waqf Mujawwaz) - permissible to stop, but continuing is slightly preferred.',
      'ص (Waqf Murakhkhas) - only stop here if necessary, such as running out of breath; continuing is preferred.',
      '∴  ∴ (Waqf Muraqabah, "the twin pauses") - two sets of three dots appear close together; stop at one or the other, never both, since together they represent alternate valid readings of the same passage.',
    ],
    colorLegend: [{ color: 'neutral', label: 'Waqf symbol' }],
  },
]
